import api from '@/http/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useNavigate, useParams } from 'react-router-dom';
import CustomerHeader from '@univdiam/ui/customComponents/OrderDetail/CustomerHeader';
import OrderDetails from '@univdiam/ui/customComponents/OrderDetail/OrderDetails';
import type { OrderInstruction, OrderType } from '@/types';
import SpecialInstructions from '@univdiam/ui/customComponents/OrderDetail/SpecialInstructions';
import Attachments from '@univdiam/ui/customComponents/OrderDetail/Attachments';
import AdditionalInstruction from '@univdiam/ui/customComponents/OrderDetail/AdditionalInstruction';
import CadDesign from '@univdiam/ui/customComponents/OrderDetail/CadDesign';
import TimelineLogSection from '@univdiam/ui/customComponents/OrderDetail/TimelineLogSection';
import { useOrderInst } from '@/sharedData/Order';
import Chat from './Chat';
import { useSocketStore } from '@/socketStore';
import { useEffect } from 'react';
import Loader from '@/components/Loader';
import SectionHeader from '@univdiam/ui/customComponents/OrderDetail/SectionHeader';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@univdiam/ui/components/table';
import { format } from 'date-fns';
import { useAuthStore } from '@/store';
import { PackageCheck } from 'lucide-react';

const getOrder = (id: string, customer_id?: string) => {
  const url = customer_id ? `/order/${id}?customer_id=${customer_id}` : `/order/${id}`;
  return api.get(url);
};
const getOrderLog = (order_id: string) => {
  return api.get(`/log/order/${order_id}`);
};
const getOrderCad = (order_id: string) => {
  return api.get(`/cad/${order_id}`);
};
const getOrderCadSubmiited = (order_id: string) => {
  return api.get(`/cad_order_req/${order_id}`);
};

type CADDesignType = {
  bom: JSON;
  attachments: JSON;
  cost_price: string;
  selling_price: string;
  created_at: string
}


const OrderDetail = () => {
  const { id, customer_id } = useParams();
  const navigate = useNavigate()
  const { user, setNotificationCount } = useAuthStore()

  const { data, isLoading } = useQuery({
    queryKey: ["order", id, customer_id],
    queryFn: async () => {
      return await getOrder(id!, customer_id);
    },
    enabled: !!id,
  });


  const orderDetailData = data?.data?.data as OrderType

  const header = {
    JobTitle: orderDetailData?.job_title,
    status: orderDetailData?.status,
    designType: orderDetailData?.design_type
  }

  const OrderDetail = {
    orderID: orderDetailData?.order_id,
    orderDate: orderDetailData?.created_at,
    ResinSampleModel: Boolean(orderDetailData?.resin_sample_model),
    Size: orderDetailData?.size,
    TypeofJewelry: orderDetailData?.product_type,
    MetalType: orderDetailData?.metal_type,
    DesignType: orderDetailData?.design_type || '',
    DiamondQuality: orderDetailData?.diamond_quality || '',
    Quantity: orderDetailData?.quantity,
    labels: orderDetailData?.labels,
  }


  const { data: instructions, isLoading: isInstructionsLoading } = useOrderInst(orderDetailData?.id.toString());

  const orderInstruction = instructions?.data?.data as OrderInstruction[]


  const { data: orderLog, isLoading: isOrderLogLoading } = useQuery({
    queryKey: ["order-log", orderDetailData?.id?.toString()],
    queryFn: async () => {
      return await getOrderLog(orderDetailData?.id?.toString());
    },
    enabled: !!orderDetailData?.id,
  });

  const { data: orderCad, isLoading: isOrderCadLoading } = useQuery({
    queryKey: ["order-cad", orderDetailData?.id?.toString()],
    queryFn: async () => {
      return await getOrderCad(orderDetailData?.id?.toString());
    },
    enabled: !!orderDetailData?.id,
  });

  const { data: orderCadSubmitted } = useQuery({
    queryKey: ["order-cad-submitted", orderDetailData?.id?.toString()],
    queryFn: async () => {
      return await getOrderCadSubmiited(orderDetailData?.id?.toString());
    },
    enabled: !!orderDetailData?.id,
  });
  const submittedOrders = orderCadSubmitted?.data?.cad_orders


  const CADDesigns = orderCad?.data?.data?.map((design: CADDesignType, index: number) => ({
    ...design,
    fileName: `Product design_${orderDetailData?.order_id}_${index + 1}`
  }))

  const order_activity_log = orderLog?.data?.logs


  const joinOrderChat = useSocketStore((s) => s.joinOrderChat);
  const leaveOrderChat = useSocketStore((s) => s.leaveOrderChat);

  useEffect(() => {
    if (!orderDetailData?.order_id) return;
    const orderId = orderDetailData.order_id;
    joinOrderChat(orderId);
    // Leaving on unmount matters more than it used to: the socket store now
    // remembers joined rooms so it can restore them after a reconnect. Without
    // this, navigating away by any route other than handleBack leaves the id in
    // that set forever, and every later reconnect re-joins every order the
    // session has ever opened.
    return () => {
      leaveOrderChat(orderId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetailData?.order_id]);

  const handleBack = () => {
    leaveOrderChat(orderDetailData.order_id)
    navigate(-1)

  }

  const navigateToCadDesign = (id: string) => {
    const product = CADDesigns.find((d: { id: string }) => d.id === id)
    navigate(`/product-view/${id}?from=CAD&order_id=${orderDetailData?.id}`, {
      state: { ...product },
    })
  }
  const navigateToCadDesignSubmmited = (order: Record<string, unknown>) => {
    navigate(`/submitted-order/${order.id}?d=${encodeURIComponent(JSON.stringify(order))}`)
  }

  useEffect(() => {
    if (orderDetailData) {
      if (orderDetailData.is_new_update) {
        api.post(`/order/update/${orderDetailData?.id}/${orderDetailData?.customer_id}`, {
          is_new_update: false,
          orderRef: orderDetailData?.id,
        });
      }

    }
  }, [orderDetailData])
  const sendNotification = useSocketStore((s) => s.sendNotification)
  const queryClient = useQueryClient()

  // Chat.tsx will handle the update-notif-chat when it mounts.

  if (isLoading) {
    return <Loader />
  }

  return (
    <div className='flex flex-col lg:flex-row grow-0 gap-4 p-4 justify-center items-start relative w-full'>
      <div className='flex flex-col gap-4 w-full lg:w-[65%] order-1 lg:order-none pb-24 lg:pb-0'>
        <CustomerHeader orderDetail={header} handleBack={handleBack} />
        {
          submittedOrders?.length > 0 && <div className='bg-white border border-[#E9EAEB] rounded-md overflow-hidden border-b-1'>
            <SectionHeader title="Submitted order" sectionIcon={<PackageCheck strokeWidth={1.5} className="w-5 h-5 text-gray-600" />} />
            <div className='text-body2 w-full'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className='px-4'>Submitted at</TableHead>
                    <TableHead className='px-4'>Product ID</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className='text-body2'>
                  {
                    submittedOrders?.map((order: Record<string, unknown>) => (
                      <TableRow key={order.id as string} className='my-2 cursor-pointer' onClick={() => {
                        navigateToCadDesignSubmmited(order)
                      }}>
                        <TableCell className='px-4'>{format(new Date(order.created_at as string), 'dd MMM yyyy')}</TableCell>
                        <TableCell className='px-4'>{order.productId as string}</TableCell>
                        <TableCell>
                          <p>View</p>
                        </TableCell>
                      </TableRow>
                    ))
                  }
                </TableBody>
              </Table>

            </div>
          </div>
        }
        <div className='flex flex-col gap-4'>
          <OrderDetails order={OrderDetail} isAdmin={false} />
          <SpecialInstructions specialInstructions={orderDetailData?.special_instructions} />
          <Attachments attachmentLinks={orderDetailData?.media_attachment} isAdmin={true} />
          <AdditionalInstruction AdditionalInstructions={orderInstruction || []} isLoading={isInstructionsLoading} isAdmin={false} />
          <CadDesign CADDesign={CADDesigns} isAdmin={true} isOrderCadLoading={isOrderCadLoading} navigateToCadDesign={navigateToCadDesign} />
          <TimelineLogSection Timeline={order_activity_log || []} isAdmin={false} isLoading={isOrderLogLoading} />
        </div>
      </div>
      <div className='hidden lg:block w-[35%] sticky top-4 self-start h-[calc(100vh-2rem)] overflow-y-auto'>
        <Chat orderDetailData={orderDetailData} />
      </div>
    </div>
  )
}

export default OrderDetail